import {
  ButtonContext,
  DynamicDataList,
  DynamicListDataEntry,
  type DynamicListDataEntryProps,
  type DynamicListDataEntryRenderer,
  ImgElement,
  type ObjectSchemaDefinition,
  createSelectRenderer,
} from '@axinom/mosaic-ui';
import { IdentityProvider } from 'generated/id-service-env-administration';
import React from 'react';
import * as Yup from 'yup';
import disabled from '../../../../../../images/status/disabled.svg';
import enabled from '../../../../../../images/status/enabled.svg';
import notAvailable from '../../../../../../images/status/not_available.svg';
import type {
  FormDataIdProvider,
  FormDataInternalIdpConfiguration,
} from '../../IdentityProviders.types';
import { useGenerateInlineMenuActions } from '../helpers/useGenerateInlineMenuActions';
import classes from './Providers.module.scss';

export interface IdpOption {
  label: string;
  value: IdentityProvider;
}

interface InternalProvidersProps {
  value: FormDataInternalIdpConfiguration;
  idpOptions: IdpOption[];
  onAdd: (data: FormDataIdProvider) => void;
}

export const InternalProviders: React.FC<InternalProvidersProps> = ({
  value,
  idpOptions,
  onAdd,
}) => {
  const idpConfigStatusRenderer = (val: string): JSX.Element => {
    const status =
      value.isAvailable === false
        ? 'Not Available'
        : val === 'ENABLED'
        ? 'Enabled'
        : 'Disabled';
    const tooltip =
      value.isAvailable === false
        ? 'AxAuth Service must be enabled to use Email and Password authentication. You can enable this from Environment Details station.'
        : status;
    return (
      <div className={classes.status_container} title={tooltip}>
        <ImgElement
          src={
            value.isAvailable === false
              ? notAvailable
              : val === 'ENABLED'
              ? enabled
              : disabled
          }
          style={{ width: '20px', height: '20px' }}
          decorative={true}
        />
        <div className={classes.status}>{status}</div>
      </div>
    );
  };

  const idpSelectRenderer = (): DynamicListDataEntryRenderer => {
    return createSelectRenderer({
      options: idpOptions,
      placeholder: 'Select Identity Provider',
      name: 'newIdp',
    });
  };

  const CustomDynamicListDataEntry: React.FC<
    DynamicListDataEntryProps<FormDataIdProvider>
  > = (props) => {
    const { onActionClicked, ...rest } = props;
    return (
      <DynamicListDataEntry
        {...rest}
        onActionClicked={(data) => {
          onAdd({
            ...data,
            status:
              data.idpId === IdentityProvider.Custom ? 'DISABLED' : 'ENABLED',
          });
        }}
        actionButtonContext={ButtonContext.Active}
      />
    );
  };

  const idpConfigValidationSchema = Yup.object<ObjectSchemaDefinition>({
    idpId: Yup.string().required('Identity Provider is required'),
  });

  return (
    <DynamicDataList<FormDataIdProvider>
      className={classes.providers_list}
      columns={[
        {
          propertyName: 'id',
          label: 'Position',
          size: '100px',
          render: () => null,
        },
        {
          propertyName: 'idpImageUrl',
          label: 'Logo',
          size: '70px',
        },
        {
          propertyName: 'idpId',
          label: 'Provider',
          dataEntryRender: idpSelectRenderer(),
          render: () => <div>{`Email and Password (AxAuth)`}</div>,
        },
        {
          propertyName: 'displayName',
          label: 'Display Name',
        },
        {
          propertyName: 'status',
          label: 'Status',
          render: (val) => idpConfigStatusRenderer(val as string),
          tooltip: false,
        },
      ]}
      allowNewData={true}
      allowReordering={false}
      positionPropertyName="sortOrder"
      value={[value]}
      stickyHeader={false}
      customDataEntry={CustomDynamicListDataEntry}
      rowValidationSchema={idpConfigValidationSchema}
      inlineMenuActions={useGenerateInlineMenuActions}
    />
  );
};
